import React from 'react';
import { useSelector } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Col, Divider, Radio, Row } from 'antd';
import { FieldArray, useFormikContext } from 'formik';
import get from 'lodash/get';
import moment from 'moment-timezone';

import { useCustomPropertyList } from 'api/mappings';
import { TENANTS } from 'appConstants';
import DeleteIconButton from 'components/DeleteIconButton/DeleteIconButton';
import Label from 'components/Text/Label';
import {
  FItemDatePicker,
  FItemInput,
  FItemInputNumber,
  FItemRadioGroup,
  FItemSelect
} from 'components/formik';
import { makeSelectTenantConfig } from 'containers/App/selectors';
import { TRuleCreatePage } from 'containers/Rule/i18n/TModules';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import { verticalLayout } from 'utils/formItemLayouts';

import CustomFilterField from './CustomFilterField';

interface CustomFilterOperator {
  label: string;
  key: string;
  type: 'boolean' | 'input' | 'number_input' | 'select' | 'multi_select' | 'date_select';
  values?: { key: string | number; label: string }[];
  placeholder?: string;
}

interface CustomFilter {
  type: 'boolean' | 'date' | 'date_dynamic' | 'integer' | 'string';
  label: string;
  operators: CustomFilterOperator[];
  formatter?: string;
}

const monthOptions = moment.months().map((month, index) => ({ key: index + 1, label: month }));

const weekdayOptions = moment.weekdays().map((weekday, index) => ({ key: index, label: weekday }));

export const customFilterTemplates: CustomFilter[] = [
  {
    type: 'boolean',
    label: 'Boolean',
    operators: [
      {
        label: 'equals',
        key: 'eq',
        type: 'boolean'
      }
    ]
  },
  {
    type: 'date',
    label: 'Date',
    operators: [
      {
        label: 'Day of week',
        key: 'day_of_week',
        type: 'multi_select',
        values: weekdayOptions,
        placeholder: 'Select days'
      },
      {
        label: 'Equals',
        key: 'eq',
        type: 'date_select'
      },
      {
        label: 'Greater than',
        key: 'gt',
        type: 'date_select'
      },
      {
        label: 'In months',
        key: 'in_months',
        type: 'multi_select',
        values: monthOptions,
        placeholder: 'Select months'
      },
      {
        label: 'Less than',
        key: 'lt',
        type: 'date_select'
      },
      {
        label: 'Not in months',
        key: 'not_in_months',
        type: 'multi_select',
        values: monthOptions,
        placeholder: 'Select months'
      }
    ],
    formatter: 'datetime'
  },
  {
    type: 'integer',
    label: 'Number',
    operators: [
      {
        label: 'equals',
        key: 'eq',
        type: 'number_input'
      },
      {
        label: 'is greater than',
        key: 'gt',
        type: 'number_input'
      },
      {
        label: 'is less than',
        key: 'lt',
        type: 'number_input'
      }
    ]
  },
  {
    type: 'string',
    label: 'String',
    operators: [
      {
        label: 'equals',
        key: 'eq',
        type: 'input'
      },
      {
        label: 'does not equal to',
        key: 'neq',
        type: 'input'
      },
      {
        label: 'in',
        key: 'in',
        type: 'input'
      },
      {
        label: 'not_in',
        key: 'not_in',
        type: 'input'
      }
    ]
  }
];

export const customFilterDateTemplates: CustomFilter[] = [
  {
    type: 'boolean',
    label: 'Boolean',
    operators: [
      {
        label: 'equals',
        key: 'eq',
        type: 'boolean'
      }
    ]
  },
  {
    type: 'date',
    label: 'Date (Static)',
    operators: [
      {
        label: 'Day of week',
        key: 'day_of_week',
        type: 'multi_select',
        values: weekdayOptions,
        placeholder: 'Select days'
      },
      {
        label: 'Equals',
        key: 'eq',
        type: 'date_select'
      },
      {
        label: 'Greater than',
        key: 'gt',
        type: 'date_select'
      },
      {
        label: 'In months',
        key: 'in_months',
        type: 'multi_select',
        values: monthOptions,
        placeholder: 'Select months'
      },
      {
        label: 'Less than',
        key: 'lt',
        type: 'date_select'
      },
      {
        label: 'Not in months',
        key: 'not_in_months',
        type: 'multi_select',
        values: monthOptions,
        placeholder: 'Select months'
      }
    ],
    formatter: 'datetime'
  },
  {
    type: 'date_dynamic',
    label: 'Date (Dynamic)',
    operators: [
      {
        label: 'Is Less than',
        key: 'lt',
        type: 'date_select'
      },
      {
        label: 'Is NOT Less than',
        key: 'nlt',
        type: 'date_select'
      },
      {
        label: 'Is Less or Equal to',
        key: 'lteq',
        type: 'date_select'
      },
      {
        label: 'Is NOT Less or Equal to',
        key: 'nlteq',
        type: 'date_select'
      },
      {
        label: 'Is More than',
        key: 'gt',
        type: 'date_select'
      },
      {
        label: 'Is NOT More than',
        key: 'ngt',
        type: 'date_select'
      },
      {
        label: 'Is More or Equal to',
        key: 'gteq',
        type: 'date_select'
      },
      {
        label: 'Is NOT More or Equal to',
        key: 'ngteq',
        type: 'date_select'
      }
    ],
    formatter: 'datetime'
  },
  {
    type: 'integer',
    label: 'Number',
    operators: [
      {
        label: 'equals',
        key: 'eq',
        type: 'number_input'
      },
      {
        label: 'is greater than',
        key: 'gt',
        type: 'number_input'
      },
      {
        label: 'is less than',
        key: 'lt',
        type: 'number_input'
      }
    ]
  },
  {
    type: 'string',
    label: 'String',
    operators: [
      {
        label: 'equals',
        key: 'eq',
        type: 'input'
      },
      {
        label: 'does not equal to',
        key: 'neq',
        type: 'input'
      },
      {
        label: 'in',
        key: 'in',
        type: 'input'
      },
      {
        label: 'not_in',
        key: 'not_in',
        type: 'input'
      }
    ]
  }
];

const CUSTOM_FILTER_DATE_TEMPLATES = ['rule_builder_rules', 'earn_rules', 'campaign_trigger'];

const Personal = ({ flag }) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const tenantConfig = useSelector(makeSelectTenantConfig());
  const typeOptions = (
    CUSTOM_FILTER_DATE_TEMPLATES.includes(flag) ? customFilterDateTemplates : customFilterTemplates
  ).map(({ type, label }) => ({ value: type, label }));

  const { data } = useCustomPropertyList();

  const customAttributesArrayPath = `filters.custom_attributes.0.criteria`;

  const { t } = useMultiLanguageTranslation();

  return (
    <>
      <FieldArray
        name={customAttributesArrayPath}
        render={({ push, remove }) => {
          return (
            <div style={{ maxWidth: 600 }}>
              {get(values, customAttributesArrayPath)
                ?.map((_, index) => {
                  const fieldPath = `${customAttributesArrayPath}.${index}`;
                  const type = get(values, `${fieldPath}.type`);

                  const selectedFilterTemplate = (
                    CUSTOM_FILTER_DATE_TEMPLATES.includes(flag)
                      ? customFilterDateTemplates
                      : customFilterTemplates
                  ).find(item => item.type === type);

                  const operatorOptions = selectedFilterTemplate?.operators?.map(item => ({
                    value: item.key,
                    label: item.label
                  }));

                  const operator = get(values, `${fieldPath}.operator`);
                  const selectedOperatorTemplate = selectedFilterTemplate?.operators.find(
                    item => item.key === operator
                  );
                  const selectedOperatorType = selectedOperatorTemplate?.type;

                  return (
                    <div key={index} style={{ position: 'relative' }}>
                      <DeleteIconButton
                        onClick={() => remove(index)}
                        style={{ position: 'absolute', top: 8, right: -24 }}
                      />
                      <FItemSelect
                        name={`${fieldPath}.type`}
                        aria-label={t(
                          `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CustomAttributeType`,
                          {
                            index: index + 1
                          }
                        )}
                        options={typeOptions}
                        onChange={value => {
                          setFieldValue(`${fieldPath}.type`, value);
                          setFieldValue(`${fieldPath}.attribute`, undefined);
                          setFieldValue(
                            `${fieldPath}.operator`,
                            value === 'boolean' ? 'eq' : undefined
                          );
                          setFieldValue(`${fieldPath}.selected_values`, undefined);
                          setFieldValue(
                            `${fieldPath}.period`,
                            value === 'date_dynamic' ? 'days' : undefined
                          );
                        }}
                      />
                      {type !== 'date_dynamic' && (
                        <FItemInput
                          name={`${fieldPath}.attribute`}
                          aria-label={t(
                            `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CustomAttribute`,
                            {
                              index: index + 1
                            }
                          )}
                          label={t(
                            `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.EnterKeyNameAndValueDetails`
                          )}
                          placeholder={t(
                            `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.KeyNamePlaceholder`
                          )}
                          formItemProps={{ ...verticalLayout }}
                          style={{ width: '100%' }}
                        />
                      )}
                      {type === 'date_dynamic' && (
                        <Row>
                          <Col span={24} style={{ marginBottom: '4px' }}>
                            <span
                              style={{
                                fontWeight: 500,
                                fontSize: '14px',
                                display: 'block'
                              }}
                            >
                              {t(
                                `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CurrentTimestamp`
                              )}
                            </span>
                          </Col>
                        </Row>
                      )}
                      <Row gutter={16}>
                        {type !== 'boolean' && (
                          <Col xs={24} md={12}>
                            <div
                              style={
                                type === 'date_dynamic'
                                  ? {
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      width: '100%',
                                      whiteSpace: 'nowrap',
                                      marginBottom: '-20px'
                                    }
                                  : {}
                              }
                            >
                              <FItemSelect
                                name={`${fieldPath}.operator`}
                                aria-label={t(
                                  `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CustomAttributeOperator`,
                                  {
                                    index: index + 1
                                  }
                                )}
                                style={type === 'date_dynamic' ? { width: '200px' } : {}}
                                options={operatorOptions}
                                onChange={value => {
                                  setFieldValue(`${fieldPath}.operator`, value);
                                  setFieldValue(`${fieldPath}.selected_values`, undefined);
                                }}
                              />
                              {type === 'date_dynamic' && (
                                <>
                                  <FItemInputNumber
                                    name={`${fieldPath}.selected_values`}
                                    min={0}
                                    step={1}
                                    precision={0}
                                    aria-label={t(
                                      `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CustomAttributeValue`,
                                      {
                                        index: index + 1
                                      }
                                    )}
                                  />
                                  <FItemSelect
                                    name={`${fieldPath}.period`}
                                    style={{ width: '100px' }}
                                    aria-label={t(
                                      `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CustomAttributeValue`,
                                      {
                                        index: index + 1
                                      }
                                    )}
                                    options={[
                                      { value: 'days', label: 'Days' },
                                      { value: 'months', label: 'Months' },
                                      { value: 'years', label: 'Years' }
                                    ]}
                                  />
                                </>
                              )}
                            </div>
                          </Col>
                        )}
                        {type === 'date_dynamic' ? (
                          <></>
                        ) : (
                          <Col xs={24} md={12}>
                            {type === 'boolean' && (
                              <FItemRadioGroup
                                name={`${fieldPath}.selected_values`}
                                aria-label={t(
                                  `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CustomAttributeValue`,
                                  {
                                    index: index + 1
                                  }
                                )}
                              >
                                <Label bold>
                                  {t(
                                    `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.Is`
                                  )}
                                </Label>
                                <Radio value={true}>
                                  {t(
                                    `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.True`
                                  )}
                                </Radio>
                                <Radio value={false}>
                                  {t(
                                    `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.False`
                                  )}
                                </Radio>
                              </FItemRadioGroup>
                            )}
                            {type === 'integer' && (
                              <FItemInputNumber
                                name={`${fieldPath}.selected_values`}
                                aria-label={t(
                                  `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CustomAttributeValue`,
                                  {
                                    index: index + 1
                                  }
                                )}
                                min={0}
                                step={1}
                                style={{ width: '100%' }}
                                placeholder={t(
                                  `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.EnterNumber`
                                )}
                              />
                            )}
                            {type === 'string' && (
                              <FItemInput
                                name={`${fieldPath}.selected_values`}
                                aria-label={t(
                                  `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CustomAttributeValue`,
                                  {
                                    index: index + 1
                                  }
                                )}
                                style={{ width: '100%' }}
                                placeholder={t(
                                  `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.KeyValue`
                                )}
                              />
                            )}
                            {type === 'date' && (
                              <>
                                {selectedOperatorType === 'date_select' && (
                                  <FItemDatePicker
                                    name={`${fieldPath}.selected_values`}
                                    aria-label={t(
                                      `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CustomAttributeValue`,
                                      {
                                        index: index + 1
                                      }
                                    )}
                                    momentFormat="DD-MM-YYYY"
                                    placeholder={t(
                                      `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.DatePlaceholder`
                                    )}
                                    format="DD-MM-YYYY"
                                  />
                                )}
                                {selectedOperatorType === 'select' && (
                                  <FItemSelect
                                    name={`${fieldPath}.selected_values`}
                                    aria-label={t(
                                      `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CustomAttributeValue`,
                                      {
                                        index: index + 1
                                      }
                                    )}
                                    options={selectedOperatorTemplate.values?.map(
                                      ({ key, label }) => ({
                                        value: key,
                                        label
                                      })
                                    )}
                                    placeholder={selectedOperatorTemplate.placeholder}
                                  />
                                )}
                                {selectedOperatorType === 'multi_select' && (
                                  <FItemSelect
                                    mode="multiple"
                                    name={`${fieldPath}.selected_values`}
                                    aria-label={t(
                                      `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CustomAttributeValue`,
                                      {
                                        index: index + 1
                                      }
                                    )}
                                    options={selectedOperatorTemplate.values?.map(
                                      ({ key, label }) => ({
                                        value: key,
                                        label
                                      })
                                    )}
                                    placeholder={selectedOperatorTemplate.placeholder}
                                  />
                                )}
                              </>
                            )}
                          </Col>
                        )}
                      </Row>
                      {type === 'date_dynamic' && (
                        <p
                          style={{
                            fontWeight: 500,
                            fontSize: '14px'
                          }}
                        >
                          {t(
                            `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.VsTimestampKeyInPersonalProperties`
                          )}
                        </p>
                      )}
                      {type === 'date_dynamic' && (
                        <FItemInput
                          name={`${fieldPath}.attribute`}
                          aria-label={t(
                            `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.CustomAttribute`,
                            {
                              index: index + 1
                            }
                          )}
                          label={t(
                            `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.EnterTimestampKeytoCheck`
                          )}
                          placeholder={t(
                            `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.KeyNamePlaceholder`
                          )}
                          formItemProps={{ ...verticalLayout, style: { marginBottom: '10px' } }}
                          style={{ width: '100%' }}
                        />
                      )}
                      {type === 'date_dynamic' && (
                        <Row>
                          <Col span={24} style={{ marginTop: '4px', marginBottom: '6px' }}>
                            <Alert
                              message={t(
                                `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.KeyMustBeIN`
                              )}
                              type="info"
                              showIcon
                              data-testid="streak-summary-info"
                            />
                          </Col>
                        </Row>
                      )}
                    </div>
                  );
                })
                .reduce(
                  (acc, item, index) =>
                    acc.length === 0
                      ? [item]
                      : [...acc, <Divider key={`divider-${index}`} />, item],
                  []
                )}
              <Button
                type="primary"
                ghost
                onClick={() =>
                  push({
                    type: 'string',
                    attribute: null,
                    operator: null,
                    selected_values: null
                  })
                }
                icon={<PlusOutlined />}
              >
                {t(
                  `${TRuleCreatePage}.LoyaltyAudience.AudienceFilters.PersonalFilter.AddCustomProperties`
                )}
              </Button>
              <Divider />
            </div>
          );
        }}
      />
      {tenantConfig && tenantConfig.name === TENANTS.STARHUB && (
        <>
          &nbsp;&nbsp;
          <Checkbox
            checked={!!values.membership}
            onChange={evt => {
              const v = evt.target.checked
                ? [
                    {
                      type: 'membership',
                      criteria: {
                        eq: 'Hub Club'
                      }
                    }
                  ]
                : null;
              setFieldValue('membership', v);
            }}
          />
          <Divider />
        </>
      )}
      {data
        ?.map(item => <CustomFilterField key={item.key} fieldMapping={item} />)
        ?.reduce(
          (acc, item, index) =>
            !acc?.length ? [item] : [...acc, <Divider key={`divider-${index}`} />, item],
          []
        )}
    </>
  );
};

export default Personal;
